import "./ZeosSimpleList.css";

import React, { useContext, useEffect, useRef, useState } from "react";

import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { IListItemSimple } from "../../../services/zeos-api/contracts/ListUtility";
import { ZeosListEnum } from "../../../services/zeos-api/enums/ListUtility";

interface IZeosDropListProps {
  className?: string;
  clearable?: boolean;
  disabled?: boolean;
  defaultValue?: IListItemSimple;
  listDefinition: ZeosListEnum | IListItemSimple[];
  maxHeight?: number;
  name?: string;
  onChange: (item: IListItemSimple | null) => void;
  onSearch?: (searchTerm: string) => void;
  placeholder?: string;
  required?: boolean;
  searchable?: boolean;
}

export const ZeosSimpleList = (props: IZeosDropListProps) => {
  const {
    className = "",
    clearable = false,
    defaultValue,
    disabled = false,
    listDefinition: list,
    maxHeight = 200,
    name,
    onChange,
    onSearch,
    placeholder = "Select an item...",
    required = false,
    searchable = false,
  } = props;

  const data = useContext(ZeosApiContext);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [listItems, setListItems] = useState<IListItemSimple[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState<IListItemSimple | null>(
    null
  );
  const [filteredItems, setFilteredItems] =
    useState<IListItemSimple[]>(listItems);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        if (Array.isArray(list)) {
          setListItems(list);
        } else {
          const result = await data.ListUtility.getListAsync(list);
          if (result.data != undefined && result.data.length > 0) {
            setListItems(result.data);
          }
        }
      } catch (error) {
        console.error("Failed to load list:", error);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Find selected item based on value
  useEffect(() => {
    const item =
      listItems.find((item) => item.value === defaultValue?.value) || null;
    setSelectedItem(item);
  }, [defaultValue, listItems]);

  // Filter items based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredItems(listItems);
    } else {
      const filtered = listItems.filter((item) =>
        item.display.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  }, [searchTerm, listItems]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleToggle = () => {
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm("");
      }
    }
  };

  const handleItemSelect = (item: IListItemSimple) => {
    setSelectedItem(item);
    onChange(item);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedItem(null);
    onChange(null);
    setSearchTerm("");
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    onSearch?.(term);
  };

  const displayText = selectedItem ? selectedItem.display : placeholder;

  return (
    <div className={`zeos-droplist ${className}`} ref={dropdownRef}>
      <input type="hidden" name={name} value={selectedItem?.value || ""} />

      <div
        className={`droplist-trigger ${isOpen ? "open" : ""} ${
          disabled ? "disabled" : ""
        } ${loading ? "loading" : ""}`}
        onClick={handleToggle}
      >
        <span className="droplist-text">
          {loading ? "Loading..." : displayText}
        </span>

        <div className="droplist-actions">
          {clearable && selectedItem && !disabled && (
            <button
              type="button"
              className="clear-button"
              onClick={handleClear}
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
          <span className={`dropdown-arrow ${isOpen ? "up" : "down"}`}>▼</span>
        </div>
      </div>

      {isOpen && !loading && (
        <div className="droplist-menu" style={{ maxHeight }}>
          {searchable && (
            <div className="search-container">
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="droplist-options">
            {filteredItems.length === 0 ? (
              <div className="no-options">No items found</div>
            ) : (
              filteredItems.map((item) => (
                <div
                  key={item.value}
                  className={`droplist-option ${
                    selectedItem?.value === item.value ? "selected" : ""
                  }`}
                  onClick={() => handleItemSelect(item)}
                >
                  {item.display}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
